/************************************************************************
 * Design Token Update Functions
 ************************************************************************/

import type { UpdateDesignTokensChange } from "@/store/zustand/changeLedgerTypes";
import type { ApplyInstantUpdateProps } from "./utils";

// Find which stylesheet contains a specific CSS rule
const findStyleSheetContainingRule = (
  doc: Document,
  storyboardId: string,
  selector: string,
): CSSStyleSheet | null => {
  const stylesheets = Array.from(doc.styleSheets);

  for (
    let stylesheetIndex = stylesheets.length - 1;
    stylesheetIndex >= 0;
    stylesheetIndex--
  ) {
    const stylesheet = stylesheets[stylesheetIndex];
    try {
      if (!stylesheet.cssRules) continue;

      const rules = Array.from(stylesheet.cssRules);
      let ruleIndex = -1;
      for (let i = rules.length - 1; i >= 0; i--) {
        // @ts-expect-error - if we filter for instanceof CSSStyleRule, we can't access the selectorText property
        if (rules[i]?.selectorText === selector) {
          ruleIndex = i;
          break;
        }
      }
      if (ruleIndex !== -1) {
        return stylesheet;
      }
    } catch (error) {
      // Skip stylesheets we can't access (CORS issues?)
      console.warn(
        `[applyClassProperties] Skipping stylesheet at index ${stylesheetIndex} because of error: ${error.toString()}`,
      );
    }
  }

  return null;
};

// Get existing dedicated stylesheet for design tokens (don't create if missing)
const getExistingDesignTokenStyleSheet = (
  doc: Document,
): HTMLStyleElement | null => {
  return doc.getElementById("tempo-instant-styles") as HTMLStyleElement | null;
};

// Check if a CSS rule already has the correct content
const ruleHasCorrectCSS = (rule: CSSRule, expectedCSS: string): boolean => {
  if (!(rule instanceof CSSStyleRule)) return false;

  // Normalize CSS by removing extra whitespace and semicolons
  const normalizeCSS = (css: string) =>
    css.replace(/\s+/g, " ").replace(/;\s*$/, "").trim();

  const currentCSS = normalizeCSS(rule.style.cssText);
  const expectedNormalized = normalizeCSS(expectedCSS);

  return currentCSS === expectedNormalized;
};

// Update a single CSS rule efficiently
const updateSingleCSSRule = (
  styleSheet: CSSStyleSheet,
  stylesheetIndex: number,
  selector: string,
  newCSS: string,
): { stylesheetIndex: number; ruleIndex: number } => {
  // Find existing rule index (search in reverse for CSS specificity)
  const rules = Array.from(styleSheet.cssRules || styleSheet.rules);
  let existingRuleIndex = -1;
  for (let i = rules.length - 1; i >= 0; i--) {
    // @ts-expect-error - if we filter for instanceof CSSStyleRule, we can't access the selectorText property
    if (rules[i]?.selectorText === selector) {
      existingRuleIndex = i;
      break;
    }
  }

  // Check if the existing rule already has the correct CSS
  if (existingRuleIndex !== -1) {
    const existingRule = rules[existingRuleIndex];
    if (ruleHasCorrectCSS(existingRule, newCSS)) {
      // Rule already has the correct CSS, no need to update
      return { stylesheetIndex, ruleIndex: existingRuleIndex };
    }

    // Delete old rule since it has different CSS
    styleSheet.deleteRule(existingRuleIndex);
  }

  // Insert new rule at the same position (or end if new)
  const insertIndex =
    existingRuleIndex !== -1 ? existingRuleIndex : styleSheet.cssRules.length;
  const fullRule = `${selector} { ${newCSS} }`;

  styleSheet.insertRule(fullRule, insertIndex);

  const result = { stylesheetIndex, ruleIndex: insertIndex };
  return result;
};

// Escape special characters in Tailwind class names for CSS selectors
const escapeTailwindSelector = (tailwindClass: string): string => {
  return tailwindClass
    .replace(/\[/g, "\\[")
    .replace(/\]/g, "\\]")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/\./g, "\\.")
    .replace(/\%/g, "\\%")
    .replace(/\!/g, "\\!")
    .replace(/\//g, "\\/")
    .replace(/#/g, "\\#");
};

// Find target stylesheet for a selector, with fallbacks
const findTargetStyleSheet = (
  iframeDocument: Document,
  storyboardId: string,
  fullSelector: string,
): { stylesheet: CSSStyleSheet; index: number } | null => {
  // First try to find existing rule in any stylesheet
  const targetStyleSheet = findStyleSheetContainingRule(
    iframeDocument,
    storyboardId,
    fullSelector,
  );

  if (targetStyleSheet) {
    const stylesheets = Array.from(iframeDocument.styleSheets);
    const index = stylesheets.indexOf(targetStyleSheet);
    return { stylesheet: targetStyleSheet, index };
  }

  // Fallback to design token stylesheet
  const tokenStyleSheet = getExistingDesignTokenStyleSheet(iframeDocument);
  if (tokenStyleSheet?.sheet) {
    const stylesheets = Array.from(iframeDocument.styleSheets);
    const index = stylesheets.indexOf(tokenStyleSheet.sheet);
    return { stylesheet: tokenStyleSheet.sheet, index };
  }

  return null;
};

// Remove a CSS rule from all accessible stylesheets
const removeRuleFromAllStylesheets = (
  iframeDocument: Document,
  storyboardId: string,
  fullSelector: string,
): boolean => {
  const stylesheets = Array.from(iframeDocument.styleSheets);
  let removed = false;

  for (const stylesheet of stylesheets) {
    try {
      if (stylesheet.cssRules) {
        const wasRemoved = removeSingleCSSRule(
          stylesheet,
          storyboardId,
          fullSelector,
        );
        if (wasRemoved) {
          removed = true;
          break; // Stop once we find and remove it
        }
      }
    } catch (error) {
      // Skip stylesheets we can't access (CORS issues)
    }
  }

  return removed;
};

// Remove a single CSS rule from the stylesheet
const removeSingleCSSRule = (
  styleSheet: CSSStyleSheet,
  storyboardId: string,
  selector: string,
): boolean => {
  const rules = Array.from(styleSheet.cssRules);
  let existingRuleIndex = -1;
  for (let i = rules.length - 1; i >= 0; i--) {
    // @ts-expect-error - if we filter for instanceof CSSStyleRule, we can't access the selectorText property
    if (rules[i]?.selectorText === selector) {
      existingRuleIndex = i;
      break;
    }
  }

  if (existingRuleIndex !== -1) {
    styleSheet.deleteRule(existingRuleIndex);
    return true;
  }

  return false;
};

// Apply design token updates to the DOM
export const applyUpdateDesignTokensChangeToDom = (
  props: ApplyInstantUpdateProps,
) => {
  const { iframeDocument, storyboardId, persistentData } = props;
  const change = props.change as UpdateDesignTokensChange;

  const { updatedTokenClasses, deletedTokenClasses, operationType } =
    change.changeFields;

  persistentData.storyboardsAppliedTo.add(storyboardId);

  try {
    // Initialize stylesheet info if not present
    if (!persistentData.instantUpdateData?.stylesheetInfo) {
      persistentData.instantUpdateData.stylesheetInfo = {};
    }

    // Handle deletions first - but capture stylesheet info before deleting
    if (operationType === "delete" && deletedTokenClasses) {
      for (const cls of deletedTokenClasses) {
        const selector = escapeTailwindSelector(cls.tailwind);
        const fullSelector = `.${selector}`;

        // Capture stylesheet info before deletion for redo operations
        const targetInfo = findTargetStyleSheet(
          iframeDocument,
          storyboardId,
          fullSelector,
        );

        if (targetInfo) {
          // Find the specific rule index (search in reverse for CSS specificity)
          const rules = Array.from(targetInfo.stylesheet.cssRules);
          let ruleIndex = -1;
          for (let i = rules.length - 1; i >= 0; i--) {
            // @ts-expect-error - if we filter for instanceof CSSStyleRule, we can't access the selectorText property
            if (rules[i]?.selectorText === fullSelector) {
              ruleIndex = i;
              break;
            }
          }

          if (ruleIndex !== -1) {
            // Store the stylesheet info before deletion
            const stylesheetInfo = {
              stylesheetIndex: targetInfo.index,
              ruleIndex: ruleIndex,
            };
            persistentData.instantUpdateData.stylesheetInfo[fullSelector] =
              stylesheetInfo;
          } else {
          }
        }

        // Now remove the rule from all stylesheets
        removeRuleFromAllStylesheets(
          iframeDocument,
          storyboardId,
          fullSelector,
        );
      }
    }

    // Handle additions and updates
    for (const cls of updatedTokenClasses) {
      const selector = escapeTailwindSelector(cls.tailwind);

      const ruleToSet = cls.css;

      // Handle variants (like hover, responsive)
      if (cls.variant && cls.variant !== "default") {
        // TODO: handle variants
      } else {
        const fullSelector = `.${selector}`;

        // Find existing stylesheet - don't create new one
        const targetInfo = findTargetStyleSheet(
          iframeDocument,
          storyboardId,
          fullSelector,
        );

        if (targetInfo) {
          const ruleInfo = updateSingleCSSRule(
            targetInfo.stylesheet,
            targetInfo.index,
            fullSelector,
            ruleToSet,
          );

          // Store stylesheet info in the change object
          persistentData.instantUpdateData.stylesheetInfo[fullSelector] =
            ruleInfo;
        } else {
          console.warn(
            `[applyClassProperties] No target stylesheet found for ${fullSelector}`,
          );
        }
      }
    }

    // Mark the change as successful
    persistentData.instantUpdateFailed = false;
  } catch (error) {
    console.error("[applyClassProperties] Failed to update stylesheet:", error);
    persistentData.instantUpdateFailed = true;
  }
};

export const undoUpdateDesignTokensChangeToDom = (
  props: ApplyInstantUpdateProps,
): void => {
  const { iframeDocument, storyboardId, persistentData } = props;
  const change = props.change as UpdateDesignTokensChange;

  const { updatedTokenClasses, previousTokenClasses, operationType } =
    change.changeFields;
  const stylesheetInfo = persistentData.instantUpdateData?.stylesheetInfo;

  try {
    // Undo based on operation type
    if (operationType === "create") {
      // Undo create: remove the added classes using stored stylesheet info
      for (const cls of updatedTokenClasses) {
        const selector = escapeTailwindSelector(cls.tailwind);
        const fullSelector = `.${selector}`;

        // Use stored stylesheet info if available
        const storedInfo = stylesheetInfo?.[fullSelector];
        if (storedInfo) {
          const stylesheets = Array.from(iframeDocument.styleSheets);
          const targetStylesheet = stylesheets[storedInfo.stylesheetIndex];
          if (targetStylesheet) {
            removeSingleCSSRule(targetStylesheet, storyboardId, fullSelector);
          } else {
            console.warn(
              `[DEBUG] Target stylesheet not found at index ${storedInfo.stylesheetIndex}`,
            );
          }
        } else {
          // Fallback: Find and remove the rule from all stylesheets
          removeRuleFromAllStylesheets(
            iframeDocument,
            storyboardId,
            fullSelector,
          );
        }
      }
    } else if (operationType === "delete") {
      // Undo delete: restore the deleted classes using stored stylesheet info
      if (previousTokenClasses) {
        for (const cls of previousTokenClasses) {
          const selector = escapeTailwindSelector(cls.tailwind);
          const fullSelector = `.${selector}`;

          // Use stored stylesheet info if available, otherwise find target
          const storedInfo = stylesheetInfo?.[fullSelector];
          let targetInfo: { stylesheet: CSSStyleSheet; index: number } | null =
            null;

          if (storedInfo) {
            const stylesheets = Array.from(iframeDocument.styleSheets);
            const targetStylesheet = stylesheets[storedInfo.stylesheetIndex];
            if (targetStylesheet) {
              targetInfo = {
                stylesheet: targetStylesheet,
                index: storedInfo.stylesheetIndex,
              };
            } else {
              console.warn(
                `[DEBUG] Target stylesheet not found at index ${storedInfo.stylesheetIndex}`,
              );
            }
          } else {
            targetInfo = findTargetStyleSheet(
              iframeDocument,
              storyboardId,
              fullSelector,
            );
          }

          if (targetInfo) {
            const ruleInfo = updateSingleCSSRule(
              targetInfo.stylesheet,
              targetInfo.index,
              fullSelector,
              cls.css,
            );
            // Update stylesheet info for future operations (like redo)
            if (!persistentData.instantUpdateData.stylesheetInfo) {
              persistentData.instantUpdateData.stylesheetInfo = {};
            }
            persistentData.instantUpdateData.stylesheetInfo[fullSelector] =
              ruleInfo;
          } else {
            console.warn(
              `[DEBUG] No target stylesheet found for ${fullSelector} - rule restoration failed`,
            );
          }
        }
      } else {
        console.warn(
          `[DEBUG] No previousTokenClasses available for DELETE undo operation`,
        );
      }
    } else if (operationType === "update") {
      // Undo update: restore previous values
      if (previousTokenClasses) {
        // First remove the updated classes using stored stylesheet info
        for (const cls of updatedTokenClasses) {
          const selector = escapeTailwindSelector(cls.tailwind);
          const fullSelector = `.${selector}`;

          const storedInfo = stylesheetInfo?.[fullSelector];
          if (storedInfo) {
            const stylesheets = Array.from(iframeDocument.styleSheets);
            const targetStylesheet = stylesheets[storedInfo.stylesheetIndex];
            if (targetStylesheet) {
              removeSingleCSSRule(targetStylesheet, storyboardId, fullSelector);
            } else {
              console.warn(
                `[DEBUG] Target stylesheet not found for updated rule removal`,
              );
            }
          } else {
            // Fallback: Remove from all stylesheets
            removeRuleFromAllStylesheets(
              iframeDocument,
              storyboardId,
              fullSelector,
            );
          }
        }

        // Then restore the previous values using stored stylesheet info
        for (const cls of previousTokenClasses) {
          const selector = escapeTailwindSelector(cls.tailwind);
          const fullSelector = `.${selector}`;

          // Use stored stylesheet info if available, otherwise find target
          const storedInfo = stylesheetInfo?.[fullSelector];
          let targetInfo: { stylesheet: CSSStyleSheet; index: number } | null =
            null;

          if (storedInfo) {
            const stylesheets = Array.from(iframeDocument.styleSheets);
            const targetStylesheet = stylesheets[storedInfo.stylesheetIndex];
            if (targetStylesheet) {
              targetInfo = {
                stylesheet: targetStylesheet,
                index: storedInfo.stylesheetIndex,
              };
            }
          } else {
            targetInfo = findTargetStyleSheet(
              iframeDocument,
              storyboardId,
              fullSelector,
            );
          }

          if (targetInfo) {
            const ruleInfo = updateSingleCSSRule(
              targetInfo.stylesheet,
              targetInfo.index,
              fullSelector,
              cls.css,
            );
            // Update stylesheet info for future operations
            if (!persistentData.instantUpdateData.stylesheetInfo) {
              persistentData.instantUpdateData.stylesheetInfo = {};
            }
            persistentData.instantUpdateData.stylesheetInfo[fullSelector] =
              ruleInfo;
          } else {
            console.warn(
              `[applyClassProperties] No target stylesheet found for ${fullSelector}`,
            );
          }
        }
      }
    }
  } catch (error) {
    console.error(
      "[applyClassProperties] Failed to undo stylesheet changes:",
      error,
    );
  }
};

// Consolidated apply function for design token changes
export const applyUpdateDesignTokensChange = (
  props: ApplyInstantUpdateProps,
): void => {
  // Apply DOM changes
  applyUpdateDesignTokensChangeToDom(props);
};

export const undoUpdateDesignTokensChange = (
  props: ApplyInstantUpdateProps,
): void => {
  // Undo DOM changes
  undoUpdateDesignTokensChangeToDom(props);
};
